import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllAppelOffres,
  getAppelOffreById,
  createAppelOffre,
  updateAppelOffre,
  deleteAppelOffre,
  getCandidaturesByAppelOffre, 
  searchAppelOffres, 
  getAppelOffresForCurrentUser,
} from "../services/apiService";

// Create an asynchronous action to fetch all AppelOffres
export const fetchAllAppelOffres = createAsyncThunk(
  "appelOffres/fetchAllAppelOffres",
  async (_, { rejectWithValue }) => {
    try {
      const data = await getAllAppelOffres();
      return data;
    } catch (error) {
      return rejectWithValue("Erreur de récupération des AppelOffres.");
    }
  }
);

// Create an asynchronous action to fetch an AppelOffre by ID
export const fetchAppelOffreById = createAsyncThunk(
  "appelOffres/fetchAppelOffreById",
  async (id, { rejectWithValue }) => {
    try {
      const data = await getAppelOffreById(id);
      return data;
    } catch (error) {
      return rejectWithValue("Erreur de récupération de l'AppelOffre.");
    }
  }
);

// Create an asynchronous action to create a new AppelOffre
export const createNewAppelOffre = createAsyncThunk(
  "appelOffres/createNewAppelOffre",
  async (appelOffreData, { rejectWithValue }) => {
    try {
      const data = await createAppelOffre(appelOffreData);
      return data;
    } catch (error) {
      return rejectWithValue("Erreur de création de l'AppelOffre.");
    }
  }
);

// Create an asynchronous action to update an existing AppelOffre
export const updateExistingAppelOffre = createAsyncThunk(
  "appelOffres/updateExistingAppelOffre",
  async ({ id, appelOffreData }, { rejectWithValue }) => {
    try {
      const data = await updateAppelOffre(id, appelOffreData);
      return data;
    } catch (error) {
      return rejectWithValue("Erreur de mise à jour de l'AppelOffre.");
    }
  }
);

// Create an asynchronous action to delete an AppelOffre
export const removeAppelOffre = createAsyncThunk(
  "appelOffres/removeAppelOffre",
  async (id, { rejectWithValue }) => {
    try {
      await deleteAppelOffre(id);
      return id;
    } catch (error) {
      return rejectWithValue("Erreur de suppression de l'AppelOffre.");
    }
  }
);

// Create an asynchronous action to fetch candidatures by AppelOffre ID
export const fetchCandidaturesByAppelOffre = createAsyncThunk(
  "appelOffres/fetchCandidaturesByAppelOffre",
  async (appelOffreId, { rejectWithValue }) => {
    try {
      const data = await getCandidaturesByAppelOffre(appelOffreId);
      return data;
    } catch (error) {
      return rejectWithValue("Erreur de récupération des candidatures.");
    }
  }
);

// Create an asynchronous action to search AppelOffres by keyword
export const searchAppelOffresByTitle = createAsyncThunk(
  "appelOffres/searchAppelOffres",
  async (keyword, { rejectWithValue }) => {
    try {
      const data = await searchAppelOffres(keyword);
      return data;
    } catch (error) {
      return rejectWithValue("Erreur de recherche des AppelOffres.");
    }
  }
);

// Create an asynchronous action to fetch AppelOffres for the current user
export const fetchAppelOffresForCurrentUser = createAsyncThunk(
  "appelOffres/fetchAppelOffresForCurrentUser",
  async (_, { rejectWithValue }) => {
    try {
      const data = await getAppelOffresForCurrentUser();
      return data;
    } catch (error) {
      return rejectWithValue("Erreur de récupération des AppelOffres de l'utilisateur.");
    }
  }
);

const appelOffreSlice = createSlice({
  name: "appelOffres",
  initialState: {
    appelOffres: [],
    selectedAppelOffre: null,
    candidatures: [], // Add a new state for candidatures
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Handle fetch all AppelOffres actions
      .addCase(fetchAllAppelOffres.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllAppelOffres.fulfilled, (state, action) => {
        state.loading = false;
        state.appelOffres = action.payload;
      })
      .addCase(fetchAllAppelOffres.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle fetch AppelOffre by ID actions
      .addCase(fetchAppelOffreById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAppelOffreById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedAppelOffre = action.payload;
      })
      .addCase(fetchAppelOffreById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle create new AppelOffre actions
      .addCase(createNewAppelOffre.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createNewAppelOffre.fulfilled, (state, action) => {
        state.loading = false;
        state.appelOffres.push(action.payload);
      })
      .addCase(createNewAppelOffre.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle update existing AppelOffre actions
      .addCase(updateExistingAppelOffre.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateExistingAppelOffre.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.appelOffres.findIndex(
          (appelOffre) => appelOffre.id === action.payload.id
        );
        if (index !== -1) {
          state.appelOffres[index] = action.payload;
        }
      })
      .addCase(updateExistingAppelOffre.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle remove AppelOffre actions
      .addCase(removeAppelOffre.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeAppelOffre.fulfilled, (state, action) => {
        state.loading = false;
        state.appelOffres = state.appelOffres.filter(
          (appelOffre) => appelOffre.id !== action.payload
        );
      })
      .addCase(removeAppelOffre.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle fetch candidatures by AppelOffre ID actions
      .addCase(fetchCandidaturesByAppelOffre.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCandidaturesByAppelOffre.fulfilled, (state, action) => {
        state.loading = false;
        state.candidatures = action.payload;
      })
      .addCase(fetchCandidaturesByAppelOffre.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle search AppelOffres actions
      .addCase(searchAppelOffresByTitle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchAppelOffresByTitle.fulfilled, (state, action) => {
        state.loading = false;
        state.appelOffres = action.payload;
      })
      .addCase(searchAppelOffresByTitle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle fetch AppelOffres for current user actions
      .addCase(fetchAppelOffresForCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAppelOffresForCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.appelOffres = action.payload;
      })
      .addCase(fetchAppelOffresForCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default appelOffreSlice.reducer;
